import { useAtomValue } from 'jotai';
import { Navigate, useSearchParams } from 'react-router-dom';
import { unwrappedPitchVisDisplayPresetsAtom } from '@/atoms/queries/pitchVisDisplayPresets';
import { getMetricPitchConfig, PITCH_PRESET_PARAM_KEYS, PitchVisDisplaySettings } from '@/consts/metricPitchConfig';
import { METRIC_KEY } from '@/consts/searchKeys';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';

/**
 * Applies the metric display preset when the URL contains a metricKey but no display
 * setting params — e.g. on first page load or when opening a minimal shared link.
 * Does nothing when display params are already present, so custom URL settings are preserved.
 * Returns a <Navigate replace /> element to redirect, or null if no redirect is needed.
 */
export const useInitPitchDisplayPreset = () => {
  const [searchParams] = useSearchParams();
  const pitchVisDisplayPresets = useAtomValue(unwrappedPitchVisDisplayPresetsAtom);
  const createQueryString = useUpdateQueryString(searchParams);

  const metricKey = searchParams.get(METRIC_KEY);
  const presetsLoaded = pitchVisDisplayPresets.size > 0;

  const hasDisplayParams = Object.values(PITCH_PRESET_PARAM_KEYS).some(key => searchParams.has(key));

  if (metricKey && !hasDisplayParams && presetsLoaded) {
    const presetName = pitchVisDisplayPresets.get(metricKey);
    const config = getMetricPitchConfig(presetName);

    const paramsUpdate = (Object.entries(PITCH_PRESET_PARAM_KEYS) as [keyof PitchVisDisplaySettings, string][]).map(
      ([field, paramKey]) => ({ key: paramKey, value: String(config[field]) }),
    );

    return <Navigate to={`?${createQueryString(paramsUpdate)}`} replace />;
  }

  return null;
};
